'use client'

import * as React from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

import { getUserId } from '@/helpers/getUserId'
import { getPricesKeys, normalizeQuantity } from '@/hooks/bff/prices/getPrices/queryKeys'
import { usePriceLoader } from '@/hooks/bff/prices/getPrices/usePriceLoader'
import { useAsmSession } from '@/hooks/useAsmSession'
import { useSelectedOrgUnitId } from '@/hooks/useSelectedOrgUnitId'
import { useSiteContext } from '@/hooks/useSiteContext'

import type { LoadedPrice, PriceBatchLoader } from '@/hooks/bff/prices/getPrices/priceBatchLoader'

// Prices live in the React Query cache (per-product key), not in this context — so the context value
// is stable and never re-renders consumers when a price arrives. Each component subscribes to its own
// price via useProductPrice. The methods below are a thin compatibility layer for imperative reads
// (event handlers) and prefetching from call sites that have no rendered <ProductPrice>.

interface PriceRegistryRequest {
  productId?: string
  quantity?: number
}

/** Shape kept for backward compatibility with existing getProductPrice() call sites. */
export interface ProductPricesRegistry {
  productId?: string
  priceData?: LoadedPrice
  quantity?: number
}

export interface PriceContextProviderProps {
  /** Batched loader used by useProductPrice to fetch a single product's price within a bulk request. */
  load: PriceBatchLoader['load']
  customerId: string
  orgUnitId: string
  baseSiteId: string
  pricesEnabled: boolean
  /** Synchronous peek at a cached price. Null until fetched — prefer fetchProductPrice in async handlers. */
  getProductPrice: (product: PriceRegistryRequest) => ProductPricesRegistry | null
  /**
   * Async read that guarantees the price is fetched (batched) before resolving. Use this in add-to-cart /
   * GA handlers so the price is reliably available even for off-screen items that were never rendered.
   */
  fetchProductPrice: (product: PriceRegistryRequest) => Promise<ProductPricesRegistry | null>
  /** Prefetch a product's price into the cache (for items with no rendered <ProductPrice>). */
  registerProductId: (product: PriceRegistryRequest) => void
}

const PRICE_STALE_TIME = Number.POSITIVE_INFINITY

export const PriceContext = React.createContext<PriceContextProviderProps | undefined>(undefined)

export const PriceContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const { employee, impersonatedSession } = useAsmSession()
  const { getBaseSiteId } = useSiteContext()
  const loader = usePriceLoader()

  // Employees see prices only while impersonating a customer. Requires AsmSessionContextProvider to be
  // mounted ABOVE this provider — the context default would silently report "not impersonating".
  const pricesEnabled = !employee || impersonatedSession
  const customerId = getUserId(session)
  const orgUnitId = useSelectedOrgUnitId()
  const baseSiteId = getBaseSiteId()

  const load = loader.load

  const getProductPrice = React.useCallback(
    (product: PriceRegistryRequest): ProductPricesRegistry | null => {
      if (product?.productId == null) {
        return null
      }

      const quantity = normalizeQuantity(product.quantity)
      const priceData = queryClient.getQueryData<LoadedPrice>(
        getPricesKeys.product({ customerId, orgUnitId, baseSiteId, productId: product.productId, quantity })
      )

      return { productId: product.productId, quantity, priceData: priceData ?? null }
    },
    [queryClient, customerId, orgUnitId, baseSiteId]
  )

  const registerProductId = React.useCallback(
    (product: PriceRegistryRequest) => {
      if (!pricesEnabled || product?.productId == null) {
        return
      }

      const quantity = normalizeQuantity(product.quantity)
      const productId = product.productId

      // prefetchQuery (not ensureQueryData): it populates the cache but swallows fetch errors internally,
      // so this fire-and-forget prefetch can never surface as an unhandled promise rejection.
      void queryClient.prefetchQuery({
        queryKey: getPricesKeys.product({ customerId, orgUnitId, baseSiteId, productId, quantity }),
        queryFn: () => load({ productId, quantity }),
        staleTime: PRICE_STALE_TIME,
      })
    },
    [pricesEnabled, queryClient, customerId, orgUnitId, baseSiteId, load]
  )

  const fetchProductPrice = React.useCallback(
    async (product: PriceRegistryRequest): Promise<ProductPricesRegistry | null> => {
      if (product?.productId == null) {
        return null
      }

      const quantity = normalizeQuantity(product.quantity)
      const productId = product.productId

      // Disabled (not logged in / employee without impersonation): never fetch, just report no price.
      if (!pricesEnabled) {
        return { productId, quantity, priceData: null }
      }

      try {
        // ensureQueryData returns the cached price if present, otherwise fetches (batched) and awaits it,
        // so the price is reliably available even for items that were never rendered / scrolled into view.
        const priceData = await queryClient.ensureQueryData<LoadedPrice>({
          queryKey: getPricesKeys.product({ customerId, orgUnitId, baseSiteId, productId, quantity }),
          queryFn: () => load({ productId, quantity }),
          staleTime: PRICE_STALE_TIME,
        })

        return { productId, quantity, priceData: priceData ?? null }
      } catch {
        // A price fetch failure must not break the surrounding add-to-cart flow; report no price.
        return { productId, quantity, priceData: null }
      }
    },
    [pricesEnabled, queryClient, customerId, orgUnitId, baseSiteId, load]
  )

  const contextValue = React.useMemo<PriceContextProviderProps>(
    () => ({
      load,
      customerId,
      orgUnitId,
      baseSiteId,
      pricesEnabled,
      getProductPrice,
      fetchProductPrice,
      registerProductId,
    }),
    [load, customerId, orgUnitId, baseSiteId, pricesEnabled, getProductPrice, fetchProductPrice, registerProductId]
  )

  return <PriceContext.Provider value={contextValue}>{children}</PriceContext.Provider>
}
